/**
 * ServiceFlagsEditor — admin Settings sub-page with:
 *  - Global recharge disable toggle + custom message
 *  - Global new-orders disable toggle + custom message
 *  - Off-hours ticket auto-reply editor (enabled + hours + message)
 *  - AI assistant enable/disable + model + language list
 *
 * All settings persisted in `app_config` via /api/service/*.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldAlert, Clock, Bot, Save, Loader2, Power, MessageSquare, Globe, Plus, Trash2, Key, Eye, EyeOff, CheckCircle2, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Card = ({ title, icon: Icon, color, children, dataTestId }) => (
  <section className={`bg-white rounded-xl border-2 border-${color}-200 p-5 space-y-3 shadow-sm`} data-testid={dataTestId}>
    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
      <Icon className={`w-5 h-5 text-${color}-600`} />
      <h3 className="text-base font-bold text-slate-800">{title}</h3>
    </div>
    {children}
  </section>
);

export default function ServiceFlagsEditor({ token }) {
  const [flags, setFlags] = useState(null);
  const [off, setOff] = useState(null);
  const [ai, setAi] = useState(null);
  // Separate state so the masked value returned by the backend never
  // gets re-sent. User must explicitly type a new key to update it.
  const [keyDraft, setKeyDraft] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [saving, setSaving] = useState(null);
  // Active locale tab in the Off-hours card. Defaults to English (the
  // primary copy + fallback). Local state only — admin can flip tabs
  // without touching the underlying `off.messages` object.
  const [offLang, setOffLang] = useState('en');

  const OFF_HOURS_LANGS = [
    { code: 'en',    label: 'EN' },
    { code: 'it',    label: 'IT' },
    { code: 'es',    label: 'ES' },
    { code: 'de',    label: 'DE' },
    { code: 'fr',    label: 'FR' },
    { code: 'pt',    label: 'PT' },
    { code: 'zh-CN', label: '中文' },
  ];

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    (async () => {
      const [f, o, a] = await Promise.all([
        fetch(`${API_URL}/api/service/flags`).then(r => r.json()),
        fetch(`${API_URL}/api/service/off-hours`).then(r => r.json()),
        fetch(`${API_URL}/api/service/ai-settings/admin`, { headers }).then(r => r.json()),
      ]);
      setFlags(f); setOff(o); setAi(a);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveFlags = async () => {
    setSaving('flags');
    try {
      const r = await fetch(`${API_URL}/api/service/flags`, {
        method: 'PUT', headers, body: JSON.stringify({
          recharge_disabled: !!flags.recharge_disabled,
          recharge_disabled_message: flags.recharge_disabled_message,
          orders_disabled: !!flags.orders_disabled,
          orders_disabled_message: flags.orders_disabled_message,
          recharge_show_routing: !!flags.recharge_show_routing,
          tools_enabled: !!flags.tools_enabled,
        }),
      });
      setFlags(await r.json());
    } finally { setSaving(null); }
  };

  const saveOff = async () => {
    setSaving('off');
    try {
      const r = await fetch(`${API_URL}/api/service/off-hours`, {
        method: 'PUT', headers, body: JSON.stringify({
          enabled: !!off.enabled,
          timezone_offset_hours: Number(off.timezone_offset_hours || 3),
          start_hour: Number(off.start_hour ?? 23),
          end_hour: Number(off.end_hour ?? 7),
          message: off.message,
          messages: off.messages || {},
        }),
      });
      setOff(await r.json());
    } finally { setSaving(null); }
  };

  const saveAi = async () => {
    setSaving('ai');
    try {
      const body = {
        enabled: !!ai.enabled,
        model: ai.model,
        provider: ai.provider,
        available_languages: ai.available_languages,
      };
      // Only send the key when the admin actually typed something —
      // the masked value we received on load must never round-trip.
      if (keyDraft.trim() !== '') {
        body.llm_key_override = keyDraft.trim();
      }
      const r = await fetch(`${API_URL}/api/service/ai-settings`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert('Save failed: ' + (j.detail || `HTTP ${r.status}`));
        return;
      }
      setAi(await r.json());
      setKeyDraft('');
    } finally { setSaving(null); }
  };

  const clearKey = async () => {
    if (!confirm('Clear the saved AI key? The app will fall back to the EMERGENT_LLM_KEY env variable (if set).')) return;
    setSaving('ai');
    try {
      const r = await fetch(`${API_URL}/api/service/ai-settings`, {
        method: 'PUT', headers, body: JSON.stringify({ llm_key_override: '' }),
      });
      if (r.ok) { setAi(await r.json()); setKeyDraft(''); }
    } finally { setSaving(null); }
  };

  const testKey = async () => {
    setSaving('ai-test');
    try {
      const r = await fetch(`${API_URL}/api/service/ai/test-key`, {
        method: 'POST', headers,
      });
      const raw = await r.text();
      let j = null; try { j = JSON.parse(raw); } catch { /* not JSON */ }
      if (!j) {
        alert(
          `AI key test FAILED (HTTP ${r.status}). The reverse proxy on this server ` +
          `is replacing the response body with an HTML error page, so we cannot read ` +
          `the actual LLM error. Please check backend logs: ` +
          `tail -n 100 /var/log/supervisor/backend.err.log`
        );
        return;
      }
      // FastAPI's stock 404/422 has only `detail`, not our diagnostic
      // fields. If we don't see ANY of our keys, the endpoint itself
      // is missing on the server — most likely an old code version.
      if (j.ok === undefined && (j.detail || r.status === 404)) {
        alert(
          `❌ AI key test endpoint not available on this server (HTTP ${r.status}).\n\n` +
          `This usually means the backend code is out of date.\n\n` +
          `On the production server, run:\n` +
          `  cd /app && git pull && sudo supervisorctl restart backend\n\n` +
          `Then try "Test key now" again.\n\n` +
          `Server response: ${j.detail || JSON.stringify(j)}`
        );
        return;
      }
      if (j.ok) {
        alert(
          `✅ AI key works.\n` +
          `Mode: ${j.mode === 'emergent' ? 'Emergent proxy' : 'Direct OpenAI'}\n` +
          `Provider: ${j.provider}\n` +
          `Model: ${j.model}\n` +
          `Key source: ${j.key_source} (${j.key_masked})\n` +
          `Sample translation of "hello": "${j.sample}"`
        );
      } else {
        // Tailor the hint to whichever mode was attempted so the user
        // gets actionable advice instead of generic "common causes".
        const hint = j.mode === 'emergent'
          ? (
            `Common causes (Emergent mode):\n` +
            `• 403 Forbidden — Emergent proxy blocks external server IPs.\n` +
            `  Switch to a direct OpenAI key (paste sk-proj-... or sk-... from openai.com).\n` +
            `• Out of credits — top up at Emergent Profile → Universal Key → Add Balance\n` +
            `• Wrong model — try model="gpt-4.1-mini"`
          )
          : (
            `Common causes (Direct OpenAI mode):\n` +
            `• Invalid API key — get a fresh one at platform.openai.com → API keys → Create new secret key\n` +
            `• No billing set up — add a payment method at platform.openai.com → Billing\n` +
            `• Model not enabled — try model="gpt-4o-mini" or "gpt-4.1-mini"`
          );
        alert(
          `❌ AI key test FAILED.\n\n` +
          `Mode: ${j.mode === 'emergent' ? 'Emergent proxy' : 'Direct OpenAI'}\n` +
          `Stage: ${j.stage || 'unknown'}\n` +
          `Provider: ${j.provider || '—'}\n` +
          `Model: ${j.model || '—'}\n` +
          `Key source: ${j.key_source || '—'} (${j.key_masked || '—'})\n\n` +
          `Error: ${j.error}\n\n` +
          hint
        );
      }
    } catch (e) {
      alert('AI key test FAILED: ' + (e.message || 'network error'));
    } finally { setSaving(null); }
  };

  const addLang = () => setAi(a => ({ ...a, available_languages: [...(a.available_languages || []), { code: '', name: '' }] }));
  const rmLang = (i) => setAi(a => ({ ...a, available_languages: a.available_languages.filter((_, j) => j !== i) }));
  const editLang = (i, key, val) => setAi(a => {
    const l = [...(a.available_languages || [])]; l[i] = { ...l[i], [key]: val }; return { ...a, available_languages: l };
  });

  if (!flags || !off || !ai) return <div className="p-6 text-slate-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Loading settings…</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="service-flags-editor">
      {/* Disable toggles */}
      <Card title="Service disable toggles" icon={ShieldAlert} color="rose" dataTestId="disable-toggles-card">
        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!flags.recharge_disabled}
            onChange={(e) => setFlags(f => ({ ...f, recharge_disabled: e.target.checked }))}
            data-testid="flag-recharge-disabled" className="mt-1 w-4 h-4" />
          <div className="flex-1">
            <div className="font-bold text-slate-800">Disable balance recharges globally</div>
            <div className="text-xs text-slate-500">When enabled, all customers see a maintenance banner on the Recharge page.</div>
            <textarea rows={2} value={flags.recharge_disabled_message || ''}
              onChange={(e) => setFlags(f => ({ ...f, recharge_disabled_message: e.target.value }))}
              data-testid="flag-recharge-message"
              className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
        </label>
        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!flags.orders_disabled}
            onChange={(e) => setFlags(f => ({ ...f, orders_disabled: e.target.checked }))}
            data-testid="flag-orders-disabled" className="mt-1 w-4 h-4" />
          <div className="flex-1">
            <div className="font-bold text-slate-800">Disable new order creation globally</div>
            <div className="text-xs text-slate-500">When enabled, customers see a banner on step 2 of the order wizard.</div>
            <textarea rows={2} value={flags.orders_disabled_message || ''}
              onChange={(e) => setFlags(f => ({ ...f, orders_disabled_message: e.target.value }))}
              data-testid="flag-orders-message"
              className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
        </label>
        {/* Recharge page: routing transparency card (USA / UK / EU). OFF by
            default — turn on if VAT or merchant-disclosure rules require it. */}
        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!flags.recharge_show_routing}
            onChange={(e) => setFlags(f => ({ ...f, recharge_show_routing: e.target.checked }))}
            data-testid="flag-recharge-show-routing" className="mt-1 w-4 h-4" />
          <div className="flex-1">
            <div className="font-bold text-slate-800">Show payment-routing card on Recharge page</div>
            <div className="text-xs text-slate-500">
              Reveals the "Payment will be processed by USA / UK / EU" disclosure card under PayPal on the Recharge page.
              Hidden by default — payment buttons themselves are unaffected.
            </div>
          </div>
        </label>
        {/* Tools tab visibility — gates the in-browser ECU utilities
            (Delco checksum, CTZ→BIN converter, …) behind an admin flag. */}
        <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!flags.tools_enabled}
            onChange={(e) => setFlags(f => ({ ...f, tools_enabled: e.target.checked }))}
            data-testid="flag-tools-enabled" className="mt-1 w-4 h-4" />
          <div className="flex-1">
            <div className="font-bold text-slate-800">Show "Tools" tab in admin nav</div>
            <div className="text-xs text-slate-500">
              Reveals the in-browser ECU utilities (Delco E46/E66 checksum calculator,
              Cummins CTZ → BIN converter, …). All processing is client-side; files
              never leave the operator's browser.
            </div>
          </div>
        </label>
        <button onClick={saveFlags} disabled={saving === 'flags'} data-testid="flags-save"
          className="px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {saving === 'flags' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save toggles
        </button>
      </Card>

      {/* Off-hours */}
      <Card title="Off-hours ticket auto-reply" icon={Clock} color="indigo" dataTestId="off-hours-card">
        <label className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!off.enabled}
            onChange={(e) => setOff(o => ({ ...o, enabled: e.target.checked }))}
            data-testid="off-hours-enabled" className="w-4 h-4" />
          <span className="text-sm font-bold">{off.enabled ? 'Enabled — bot replies during off hours' : 'Disabled'}</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-600">Timezone offset (hours from UTC)</span>
            <input type="number" value={off.timezone_offset_hours ?? 3}
              onChange={(e) => setOff(o => ({ ...o, timezone_offset_hours: Number(e.target.value) }))}
              data-testid="off-hours-tz"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-600">Off-hours START (hour 0–23)</span>
            <input type="number" min="0" max="23" value={off.start_hour ?? 23}
              onChange={(e) => setOff(o => ({ ...o, start_hour: Number(e.target.value) }))}
              data-testid="off-hours-start"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-600">Off-hours END (hour 0–23)</span>
            <input type="number" min="0" max="23" value={off.end_hour ?? 7}
              onChange={(e) => setOff(o => ({ ...o, end_hour: Number(e.target.value) }))}
              data-testid="off-hours-end"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </label>
        </div>
        <div className="space-y-1.5" data-testid="off-hours-i18n">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-600">Auto-reply message per language (sent when a customer writes outside working hours — picked by the user&apos;s UI language, fallback to EN)</span>
          </div>
          {/* Language tabs — picking one swaps which translation the
              textarea below edits. EN is the source-of-truth / fallback;
              empty translations fall back to EN automatically. */}
          <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-1">
            {OFF_HOURS_LANGS.map((l) => {
              const value = (off.messages || {})[l.code];
              const set = !!(value && value.trim());
              const isActive = offLang === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setOffLang(l.code)}
                  data-testid={`off-hours-tab-${l.code}`}
                  className={`px-2.5 py-1 rounded-t text-xs font-bold transition-colors flex items-center gap-1 ${
                    isActive ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={set ? `${l.label} — set` : `${l.label} — not set (falls back to EN)`}
                >
                  <span>{l.label}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${set ? 'bg-emerald-400' : 'bg-slate-400/60'}`} />
                </button>
              );
            })}
          </div>
          <textarea
            rows={4}
            value={(off.messages && off.messages[offLang]) || (offLang === 'en' ? (off.message || '') : '')}
            placeholder={offLang === 'en'
              ? '🕐 Thank you for your message. Our team is currently outside working hours…'
              : 'Leave empty to use the English fallback'}
            onChange={(e) => setOff(o => ({
              ...o,
              messages: { ...(o.messages || {}), [offLang]: e.target.value },
              // Keep the legacy single-string `message` field in sync with
              // English so older readers don't drift away from the new dict.
              ...(offLang === 'en' ? { message: e.target.value } : {}),
            }))}
            data-testid={`off-hours-message-${offLang}`}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
          />
          <p className="text-[10px] text-slate-500 italic">
            Tip: leave a non-EN locale blank to have customers in that language receive the EN copy as a safe fallback.
          </p>
        </div>
        <button onClick={saveOff} disabled={saving === 'off'} data-testid="off-hours-save"
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {saving === 'off' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save off-hours settings
        </button>
      </Card>

      {/* AI */}
      <Card title="AI assistant (tickets)" icon={Bot} color="blue" dataTestId="ai-settings-card">
        <label className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg cursor-pointer">
          <input type="checkbox" checked={!!ai.enabled}
            onChange={(e) => setAi(a => ({ ...a, enabled: e.target.checked }))}
            data-testid="ai-enabled" className="w-4 h-4" />
          <span className="text-sm font-bold">{ai.enabled ? 'Enabled — admins see "Improve text" & "Translate" buttons' : 'Disabled'}</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-600">Provider</span>
            <select value={ai.provider || 'openai'}
              onChange={(e) => setAi(a => ({ ...a, provider: e.target.value }))}
              data-testid="ai-provider"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-bold text-slate-600">Model</span>
            <input value={ai.model || ''}
              onChange={(e) => setAi(a => ({ ...a, model: e.target.value }))}
              data-testid="ai-model"
              placeholder="gpt-4.1-mini"
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
          </label>
        </div>

        {/* ── Universal LLM key (Emergent) ─────────────────────────
            Lets the operator paste / rotate the key from the UI
            instead of editing /app/backend/.env on the prod server.
            The current saved value is masked; an empty input means
            "don't change". A separate "Test" button proves the key
            works without leaving the page. */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-800">
            <Key className="w-4 h-4" /> Universal LLM Key (Emergent)
          </div>
          <div className="text-[11px] text-amber-700 leading-relaxed">
            Two key types supported (auto-detected by prefix):
            <br/>• <code>sk-emergent-…</code> → <strong>Emergent Universal Key</strong>
            (works inside Emergent preview; usually <em>blocked by 403 on self-hosted servers</em>)
            <br/>• <code>sk-proj-…</code> / <code>sk-…</code> → <strong>Direct OpenAI</strong>
            (your own key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="underline font-bold">platform.openai.com → API keys</a>;
            works from any server)
            <br/>Required for <em>Translate</em> and <em>Improve text</em>. Stored in DB — survives <code>git pull</code> / restarts.
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="font-bold text-slate-600">Current:</span>
            {ai.llm_key_source === 'db' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono" data-testid="ai-key-status">
                <CheckCircle2 className="w-3 h-3" />
                {ai.llm_key_masked || 'set'} <span className="text-emerald-600 not-italic">(saved in DB)</span>
              </span>
            )}
            {ai.llm_key_source === 'env' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-mono" data-testid="ai-key-status">
                <CheckCircle2 className="w-3 h-3" />
                {ai.llm_key_env_masked || 'set'} <span className="text-blue-600 not-italic">(from .env)</span>
              </span>
            )}
            {ai.llm_key_source === 'none' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold" data-testid="ai-key-status">
                <AlertCircle className="w-3 h-3" /> NOT CONFIGURED
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <input
              type={keyVisible ? 'text' : 'password'}
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              placeholder="sk-emergent-... (paste here to update)"
              data-testid="ai-key-input"
              autoComplete="off"
              spellCheck={false}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono bg-white" />
            <button
              type="button"
              onClick={() => setKeyVisible(v => !v)}
              data-testid="ai-key-toggle-visibility"
              className="px-2 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700"
              title={keyVisible ? 'Hide' : 'Show'}
            >
              {keyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <button
              onClick={testKey}
              disabled={saving === 'ai-test' || ai.llm_key_source === 'none'}
              data-testid="ai-key-test"
              className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded font-bold inline-flex items-center gap-1 disabled:opacity-50"
            >
              {saving === 'ai-test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Test key now
            </button>
            {ai.llm_key_source === 'db' && (
              <button
                onClick={clearKey}
                disabled={saving === 'ai'}
                data-testid="ai-key-clear"
                className="px-3 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded font-bold inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" /> Clear saved key
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-600 flex items-center gap-1"><Globe className="w-3 h-3" /> Languages available to customers for message translation</span>
            <button onClick={addLang} data-testid="ai-lang-add"
              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="space-y-1.5">
            {(ai.available_languages || []).map((l, i) => (
              <div key={i} className="flex gap-2">
                <input value={l.code || ''} onChange={(e) => editLang(i, 'code', e.target.value)}
                  placeholder="ru" data-testid={`ai-lang-code-${i}`}
                  className="w-20 px-2 py-1 border border-slate-300 rounded text-sm font-mono" />
                <input value={l.name || ''} onChange={(e) => editLang(i, 'name', e.target.value)}
                  placeholder="Russian" data-testid={`ai-lang-name-${i}`}
                  className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm" />
                <button onClick={() => rmLang(i)} data-testid={`ai-lang-remove-${i}`}
                  className="px-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <button onClick={saveAi} disabled={saving === 'ai'} data-testid="ai-save"
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {saving === 'ai' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save AI settings
        </button>
      </Card>
    </div>
  );
}
